import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { toast } from "sonner";
import {
  FileText,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Wallet,
  Percent,
  Banknote,
  RefreshCcw,
  Download,
  FileSpreadsheet,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Receipt,
  Building2,
  Users,
  Clock,
  CheckCircle,
  Gift,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import QuickPaymentDialog from "@/components/QuickPaymentDialog";
import CancellationAnalysis from "@/components/CancellationAnalysis";
import TransferPackagesReport from "@/components/TransferPackagesReport";
import GroupOccupancyReport from "@/components/GroupOccupancyReport";
import NoShowReport from "@/components/NoShowReport";
import { Zap } from "lucide-react";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

// ExportButton component - defined outside to avoid React anti-pattern
const ExportButton = ({ reportType, onExport }) => {
  const { t } = useTranslation(["common", "reports", "packages", "employees", "admin"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType, getLocalizedExpenseType } = useLocalizedData();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-lg">
          <Download className="w-4 h-4 mr-1" />
          {t("common:export")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onExport(reportType, 'excel')}>
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onExport(reportType, 'pdf')}>
          <FileText className="w-4 h-4 mr-2" />
          PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function ReportsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);
  const { currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType, getLocalizedExpenseType } = useLocalizedData();

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  // Default: Start = 1 month ago, End = Today
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  });
  const [endDate, setEndDate] = useState(() => new Date());
  
  // Report data - loaded lazily
  const [salesReport, setSalesReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  const [commissionsReport, setCommissionsReport] = useState(null);
  const [salariesReport, setSalariesReport] = useState(null);
  const [refundsReport, setRefundsReport] = useState(null);
  const [expensesReport, setExpensesReport] = useState(null);
  const [trainerIncomeReport, setTrainerIncomeReport] = useState({ rent_income: 0, commission_income: 0 });
  const [pendingRents, setPendingRents] = useState({ total_rent: 0, total_commission: 0 });
  const [bonusReport, setBonusReport] = useState(null);
  
  // Quick Payment dialog state — universal across all pending tables
  const [quickPayState, setQuickPayState] = useState(null);
  // shape: { type: 'salary'|'commission'|'rent', subject, defaultAmount, maxAmount, payload: {...}, refreshKey }
  const openQuickPay = (type, item, opts = {}) => {
    let subject, defaultAmount, maxAmount, payload, title;
    if (type === "salary") {
      subject = `${item.name || item.trainer_name || "Antrenör"} · Maaş${item.month && item.year ? ` ${item.month}/${item.year}` : ""}`;
      title = "Hızlı Maaş Ödemesi";
      defaultAmount = Number(item.remaining_amount ?? item.pending_amount ?? 0);
      maxAmount = defaultAmount;
      payload = { trainer_id: item.trainer_id, month: item.month, year: item.year };
    } else if (type === "commission") {
      subject = `${item.trainer_name || item.name || "Antrenör"} · Prim${item.month && item.year ? ` ${item.month}/${item.year}` : ""}`;
      title = "Hızlı Prim Ödemesi";
      defaultAmount = Number(item.pending_commission ?? (item.calculated_commission || 0) - (item.paid_commission || 0));
      maxAmount = defaultAmount;
      payload = { trainer_id: item.trainer_id, month: item.month, year: item.year };
    } else if (type === "rent") {
      subject = `${item.trainer_name || "Antrenör"} · Kira${item.month && item.year ? ` ${item.month}/${item.year}` : ""}`;
      title = "Hızlı Kira Tahsilatı";
      defaultAmount = Number(item.remaining_amount ?? 0);
      maxAmount = defaultAmount;
      payload = { trainer_id: item.trainer_id, month: item.month, year: item.year, debt_id: item.debt_id };
    }
    setQuickPayState({ type, title, subject, defaultAmount, maxAmount, payload, ...opts });
  };
  
  const handleQuickPaySubmit = async ({ amount, date, method, note }) => {
    if (!quickPayState) return;
    const { type, payload } = quickPayState;
    let url, body;
    if (type === "salary") {
      url = `${API}/businesses/${businessId}/salary-payments`;
      body = {
        trainer_id: payload.trainer_id,
        amount, payment_method: method, payment_date: date,
        month: payload.month, year: payload.year, notes: note,
      };
    } else if (type === "commission") {
      url = `${API}/businesses/${businessId}/commission-payments`;
      body = {
        trainer_id: payload.trainer_id,
        amount, payment_method: method, payment_date: date,
        month: payload.month, year: payload.year, notes: note,
      };
    } else if (type === "rent") {
      // Generic trainer debt collection endpoint
      url = `${API}/businesses/${businessId}/trainer-debts/${payload.trainer_id}/pay`;
      body = {
        amount, payment_method: method, payment_date: date,
        month: payload.month, year: payload.year, notes: note,
        debt_id: payload.debt_id,
      };
    }
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Ödeme kaydedilemedi");
    }
    toast.success("Ödeme başarıyla kaydedildi");
    // Trigger data reload for current tab
    setLoadedTabs({});
  };
  
  // Track which tabs have been loaded
  const [loadedTabs, setLoadedTabs] = useState({});
  
  // Reset loaded tabs when date changes
  // Tarih değiştiğinde loadedTabs'i sıfırla - bu sayede tüm veriler yeniden yüklenir
  useEffect(() => {
    setLoadedTabs({});
    // Tarih değiştiğinde tüm state'leri de sıfırla
    setSalesReport(null);
    setRevenueReport(null);
    setCommissionsReport(null);
    setSalariesReport(null);
    setRefundsReport(null);
    setExpensesReport(null);
    setTrainerIncomeReport(null);
    setPendingRents(null);
    setBonusReport(null);
  }, [startDate, endDate]);

  // Load all reports on initial render and when date changes
  useEffect(() => {
    if (businessId) {
      fetchSummaryReport();
    }
  }, [businessId, startDate, endDate]);
  
  // Load tab-specific data when tab changes (always re-fetch so results stay in
  // sync with collections/payments made on other pages — e.g. TrainerCollectionsModule).
  useEffect(() => {
    if (businessId && activeTab) {
      fetchTabReport(activeTab);
    }
  }, [businessId, activeTab, startDate, endDate]);

  // Helper to format date for API
  const formatDateForAPI = (date) => {
    if (!date) return "";
    return format(date, "yyyy-MM-dd");
  };
  
  // OPTIMIZED: Fetch ALL reports data in a SINGLE API call
  // Reduces API calls from 9 to 1 for much better performance
  const fetchSummaryReport = async () => {
    setLoading(true);
    try {
      const startStr = formatDateForAPI(startDate);
      const endStr = formatDateForAPI(endDate);
      
      console.log("Fetching all reports with optimized single API call");
      
      // SINGLE optimized API call instead of 9 separate calls
      const response = await fetch(
        `${API}/businesses/${businessId}/reports/all?start_date=${startStr}&end_date=${endStr}`,
        { credentials: "include" }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch reports");
      }
      
      const data = await response.json();
      console.log("All reports data received:", data);
      
      // Set Sales Report
      if (data.sales) {
        setSalesReport(data.sales);
      }
      
      // Set Revenue Report
      if (data.revenue) {
        setRevenueReport(data.revenue);
      }
      
      // Set Salaries Report
      if (data.salaries) {
        setSalariesReport(data.salaries);
      }
      
      // Set Refunds Report
      if (data.refunds) {
        setRefundsReport(data.refunds);
      }
      
      // Set Expenses Report
      if (data.expenses) {
        setExpensesReport(data.expenses);
      }
      
      // Set Commissions Report
      if (data.commissions) {
        setCommissionsReport(data.commissions);
      }
      
      // Set Trainer Income Report
      if (data.trainer_income) {
        setTrainerIncomeReport(data.trainer_income);
      }
      
      // Set Pending Rents
      if (data.pending_rents) {
        setPendingRents(data.pending_rents);
      }

      // Set Bonus Payments Report
      if (data.bonus_payments) {
        setBonusReport(data.bonus_payments);
      }

      // Mark all tabs as loaded
      setLoadedTabs({ summary: true, sales: true, revenue: true, commissions: true, salaries: true, refunds: true, expenses: true, bonus: true, "commission-income": true, "trainer-income": true });
    } catch (error) {
      console.error("Error fetching summary report:", error);
      toast.error(t("reports:toast_raporlarYuklenirkenHataOlustu"));
    }
    setLoading(false);
  };
  
  // Fetch tab-specific report - always fresh (no cache)
  // For salaries/expenses/refunds we now rely on the consolidated /reports/all
  // payload (which includes pending debts and bonus data) so we re-run that
  // single optimized call. Commissions still has its own dedicated endpoint.
  const fetchTabReport = async (tab) => {
    setLoading(true);
    try {
      const startStr = formatDateForAPI(startDate);
      const endStr = formatDateForAPI(endDate);
      const dateParams = `start_date=${startStr}&end_date=${endStr}`;

      switch (tab) {
        case "commissions":
        case "commission-income":
          // Commissions data is included in /reports/all consolidated payload — refresh that
          await fetchSummaryReport();
          break;

        case "salaries":
        case "expenses":
        case "refunds":
        case "bonus":
        case "trainer-income":
          // These tabs all share data from /reports/all (consolidated payload)
          await fetchSummaryReport();
          break;
      }

      setLoadedTabs(prev => ({ ...prev, [tab]: true }));
    } catch (error) {
      console.error(`Error fetching ${tab} report:`, error);
    }
    setLoading(false);
  };
  
  // OPTIMIZED: fetchReports now uses the same single API call as fetchSummaryReport
  // This is called by the "Yenile" (Refresh) button
  const fetchReports = async () => {
    // Use the same optimized function
    await fetchSummaryReport();
  };

  const handleExport = async (reportType, format) => {
    try {
      toast.info(`${format.toUpperCase()} hazırlanıyor...`);
      
      // Use as_base64=true to avoid CORS/streaming issues with proxy
      const startStr = formatDateForAPI(startDate);
      const endStr = formatDateForAPI(endDate);
      const response = await fetch(
        `${API}/businesses/${businessId}/reports/export/${reportType}?format=${format}&start_date=${startStr}&end_date=${endStr}&as_base64=true`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Decode base64 and create blob
        const byteCharacters = atob(data.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.mime_type });
        
        // Download file
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `rapor_${reportType}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(t('common:reportDownloaded'));
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || t("common:exportFailed"));
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error(t("common:exportError") + ": " + (error.message || t("common:unknownError")));
    }
  };

  const formatCurrency = (value) => {
    return `${currencySymbol}${(value || 0).toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: getDateLocale() });
    } catch {
      return dateStr;
    }
  };

  // Quick date filters
  const setThisMonth = () => {
    const today = new Date();
    setStartDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
    setEndDate(format(today, "yyyy-MM-dd"));
  };

  const setLastMonth = () => {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    setStartDate(format(lastMonth, "yyyy-MM-dd"));
    setEndDate(format(lastDayOfLastMonth, "yyyy-MM-dd"));
  };

  const setThisYear = () => {
    const today = new Date();
    setStartDate(`${today.getFullYear()}-01-01`);
    setEndDate(format(today, "yyyy-MM-dd"));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('reports:reports')}</h2>
          <p className="text-grey text-sm">{t("common:i̇sletmePerformansiniziAnalizEdin")}</p>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-grey" />
              <span className="text-sm font-medium text-grey">Tarih Aralığı:</span>
            </div>
            <div className="flex items-center gap-2">
              <DatePickerTR
                value={startDate ? new Date(startDate) : null}
                onChange={(date) => setStartDate(date ? format(date, "yyyy-MM-dd") : "")}
                placeholder={t('settings:startTime')}
                className="w-40"
              />
              <span className="text-grey">-</span>
              <DatePickerTR
                value={endDate ? new Date(endDate) : null}
                onChange={(date) => setEndDate(date ? format(date, "yyyy-MM-dd") : "")}
                placeholder={t('settings:endTime')}
                className="w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={setThisMonth} className="rounded-lg text-xs">
                {t("common:thisMonth")}
              </Button>
              <Button variant="outline" size="sm" onClick={setLastMonth} className="rounded-lg text-xs">
                {t("common:lastMonth")}
              </Button>
              <Button variant="outline" size="sm" onClick={setThisYear} className="rounded-lg text-xs">
                {t("common:thisYear")}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchReports}
              className="rounded-lg ml-auto"
            >
              <RefreshCcw className="w-4 h-4 mr-1" />
              {t("common:update")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards - Mirror tab structure exactly */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* 1) Paket Satışı */}
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("sales")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <ShoppingCart className="w-5 h-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-grey truncate">{t('payments:packagePayments')}</p>
                <p className="font-bold text-lg text-green-700 truncate">
                  {formatCurrency(salesReport?.summary?.total_sales_amount)}
                </p>
                <p className="text-xs text-green-600">{t("common:paidColon")}: {formatCurrency(salesReport?.summary?.total_paid_amount)}</p>
                <p className="text-xs text-amber-600">{t("common:pending")}: {formatCurrency((salesReport?.summary?.total_sales_amount || 0) - (salesReport?.summary?.total_paid_amount || 0))}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2) Maaş Ödemeleri */}
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("salaries")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Banknote className="w-5 h-5 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-grey truncate">{t('employees:salaryPayments')}</p>
                <p className="font-bold text-lg text-orange-600 truncate">
                  {formatCurrency((salariesReport?.summary?.total_paid_salary || 0) + (salariesReport?.summary?.total_pending_salary || 0))}
                </p>
                <p className="text-xs text-green-600">{t("common:paidColon")}: {formatCurrency(salariesReport?.summary?.total_paid_salary || 0)}</p>
                <p className="text-xs text-amber-600">{t("common:pending")}: {formatCurrency(salariesReport?.summary?.total_pending_salary || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3) Ek Ödemeler */}
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("bonus")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Gift className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-grey truncate">{t('employees:bonusPayments')}</p>
                <p className="font-bold text-lg text-purple-700 truncate">
                  {formatCurrency(bonusReport?.summary?.total_paid || 0)}
                </p>
                <p className="text-xs text-grey">{bonusReport?.summary?.count || 0} {t("common:paymentsCount", "ödeme")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 4) İşletme Giderleri */}
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("expenses")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Receipt className="w-5 h-5 text-slate-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-grey truncate">{t('payments:businessExpenses')}</p>
                <p className="font-bold text-lg text-slate-700 truncate">
                  {formatCurrency((expensesReport?.summary?.total_expenses || 0) + (expensesReport?.summary?.total_pending_expenses || 0))}
                </p>
                <p className="text-xs text-green-600">{t("common:paidColon")}: {formatCurrency(expensesReport?.summary?.total_expenses || 0)}</p>
                <p className="text-xs text-amber-600">{t("common:pending")}: {formatCurrency(expensesReport?.summary?.total_pending_expenses || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 5) Gelen Kira */}
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("trainer-income")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-grey truncate">{t("common:incomingRent")}</p>
                <p className="font-bold text-lg text-amber-700 truncate">
                  {formatCurrency((trainerIncomeReport?.rent_income || 0) + (pendingRents?.total_rent || 0))}
                </p>
                <p className="text-xs text-green-600">{t("common:paidColon")}: {formatCurrency(trainerIncomeReport?.rent_income || 0)}</p>
                <p className="text-xs text-amber-600">{t("common:pending")}: {formatCurrency(pendingRents?.total_rent || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 6) Gelen Prim */}
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("commission-income")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 rounded-lg">
                <ArrowDownRight className="w-5 h-5 text-pink-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-grey truncate">{t("reports:incomingCommission", "Gelen Prim")}</p>
                <p className="font-bold text-lg text-pink-700 truncate">
                  {formatCurrency((trainerIncomeReport?.commission_income || 0) + (pendingRents?.total_commission || 0))}
                </p>
                <p className="text-xs text-green-600">{t("common:paidColon")}: {formatCurrency(trainerIncomeReport?.commission_income || 0)}</p>
                <p className="text-xs text-amber-600">{t("common:pending")}: {formatCurrency(pendingRents?.total_commission || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 7) Primler (Verilen) */}
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("commissions")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Percent className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-grey truncate">{t("reports:commissionsGiven", "Primler")}</p>
                <p className="font-bold text-lg text-purple-700 truncate">
                  {formatCurrency((commissionsReport?.summary?.total_paid_commission || 0) + (commissionsReport?.summary?.total_pending_commission || 0))}
                </p>
                <p className="text-xs text-green-600">{t("common:paidColon")}: {formatCurrency(commissionsReport?.summary?.total_paid_commission || 0)}</p>
                <p className="text-xs text-amber-600">{t("common:pending")}: {formatCurrency(commissionsReport?.summary?.total_pending_commission || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 8) İade */}
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("refunds")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <RefreshCcw className="w-5 h-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-grey truncate">{t("common:refund")}</p>
                <p className="font-bold text-lg text-red-600 truncate">
                  {formatCurrency(refundsReport?.summary?.total_refunds)}
                </p>
                <p className="text-xs text-grey">{refundsReport?.summary?.refund_count || 0} {t("common:refundsCount")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Reports */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin scrollbar-thumb-gray-300">
          <TabsList className="bg-slate-100 rounded-xl p-1 inline-flex min-w-max gap-1">
            <TabsTrigger value="summary" className="rounded-lg data-[state=active]:bg-white whitespace-nowrap text-sm px-3 py-1.5">
              📊 {t("common:summary")}
            </TabsTrigger>
            <TabsTrigger value="sales" className="rounded-lg data-[state=active]:bg-white text-green-600 whitespace-nowrap text-sm px-3 py-1.5">
              💳 {t('payments:packagePayments')}
            </TabsTrigger>
            <TabsTrigger value="salaries" className="rounded-lg data-[state=active]:bg-white text-orange-600 whitespace-nowrap text-sm px-3 py-1.5">
              💼 {t('employees:salaryPayments')}
            </TabsTrigger>
            <TabsTrigger value="bonus" className="rounded-lg data-[state=active]:bg-white text-purple-600 whitespace-nowrap text-sm px-3 py-1.5">
              🎁 {t('employees:bonusPayments')}
            </TabsTrigger>
            <TabsTrigger value="expenses" className="rounded-lg data-[state=active]:bg-white text-red-600 whitespace-nowrap text-sm px-3 py-1.5">
              🏢 {t('payments:businessExpenses')}
            </TabsTrigger>
            <TabsTrigger value="trainer-income" className="rounded-lg data-[state=active]:bg-white text-amber-600 whitespace-nowrap text-sm px-3 py-1.5">
              🏠 {t("common:incomingRent")}
            </TabsTrigger>
            <TabsTrigger value="commission-income" className="rounded-lg data-[state=active]:bg-white text-pink-600 whitespace-nowrap text-sm px-3 py-1.5">
              📥 {t("reports:incomingCommission", "Gelen Prim")}
            </TabsTrigger>
            <TabsTrigger value="commissions" className="rounded-lg data-[state=active]:bg-white text-purple-600 whitespace-nowrap text-sm px-3 py-1.5">
              💸 {t("reports:commissionsGiven", "Primler")}
            </TabsTrigger>
            <TabsTrigger value="refunds" className="rounded-lg data-[state=active]:bg-white text-red-600 whitespace-nowrap text-sm px-3 py-1.5">
              ↩ {t("common:refund")}
            </TabsTrigger>
            <TabsTrigger value="cancellations" className="rounded-lg data-[state=active]:bg-white text-slate-700 whitespace-nowrap text-sm px-3 py-1.5" data-testid="reports-tab-cancellations">
              ✕ İptal Analizi
            </TabsTrigger>
            <TabsTrigger value="transfers" className="rounded-lg data-[state=active]:bg-white text-violet-700 whitespace-nowrap text-sm px-3 py-1.5" data-testid="reports-tab-transfers">
              ⟲ Devir Paketleri
            </TabsTrigger>
            <TabsTrigger value="group_occupancy" className="rounded-lg data-[state=active]:bg-white text-emerald-700 whitespace-nowrap text-sm px-3 py-1.5" data-testid="reports-tab-group-occupancy">
              ▦ Grup Doluluk
            </TabsTrigger>
            <TabsTrigger value="no_shows" className="rounded-lg data-[state=active]:bg-white text-rose-700 whitespace-nowrap text-sm px-3 py-1.5" data-testid="reports-tab-no-shows">
              ⊘ Gelmeyenler
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="no_shows" className="mt-4">
          <NoShowReport businessId={businessId} />
        </TabsContent>

        <TabsContent value="group_occupancy" className="mt-4">
          <GroupOccupancyReport businessId={businessId} />
        </TabsContent>

        <TabsContent value="transfers" className="mt-4">
          <TransferPackagesReport businessId={businessId} />
        </TabsContent>

        {/* Cancellation Analysis Tab */}
        <TabsContent value="cancellations" className="mt-4">
          <CancellationAnalysis
            businessId={businessId}
            startStr={formatDateForAPI(startDate)}
            endStr={formatDateForAPI(endDate)}
          />
        </TabsContent>

        {/* Summary Tab - Income vs Expenses Overview */}
        <TabsContent value="summary" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* INCOME SECTION */}
            <Card className="border-0 shadow-sm border-l-4 border-l-green-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold text-green-700 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  {t("reports:income")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Package Sales */}
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">{t("packages:paketSatislari")}</p>
                      <p className="text-xs text-green-600">{salesReport?.summary?.total_sales_count || 0} {t("common:salesCount")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-green-700">{formatCurrency(salesReport?.summary?.net_received || 0)}</p>
                </div>
                
                {/* Rent Income from Trainers */}
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">{t("common:kiraVerenAntrenorlerden")}</p>
                      <p className="text-xs text-amber-600">{t("common:gelenKiraTahsilatlari")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-amber-700">{formatCurrency(trainerIncomeReport?.rent_income || 0)}</p>
                </div>
                
                {/* Commission Income from Trainers */}
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-purple-800">{t("reports:primVerenAntrenorlerden")}</p>
                      <p className="text-xs text-purple-600">{t("reports:gelenPrimTahsilatlari")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-purple-700">{formatCurrency(trainerIncomeReport?.commission_income || 0)}</p>
                </div>
                
                {/* Total Income */}
                <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg border border-green-200">
                  <p className="font-bold text-green-800">{t("common:totalRevenue")}</p>
                  <p className="font-bold text-xl text-green-700">
                    {formatCurrency(
                      (salesReport?.summary?.net_received || 0) + 
                      (trainerIncomeReport?.rent_income || 0) + 
                      (trainerIncomeReport?.commission_income || 0)
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* EXPENSES SECTION */}
            <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold text-red-700 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5" />
                  {t("reports:expenses")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Business Expenses */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Receipt className="w-5 h-5 text-slate-600" />
                    <div>
                      <p className="font-medium text-slate-800">{t('payments:businessExpenses')}</p>
                      <p className="text-xs text-slate-600">{t("common:rentTaxDues")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-slate-700">-{formatCurrency(expensesReport?.summary?.total_expenses || 0)}</p>
                </div>
                
                {/* Salaries Paid */}
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Banknote className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium text-orange-800">{t("employees:verilenMaaslar")}</p>
                      <p className="text-xs text-orange-600">{t("employees:toTrainersAndEmployees")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-orange-700">-{formatCurrency(salariesReport?.summary?.total_paid_salary || 0)}</p>
                </div>
                
                {/* Commissions Paid */}
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Percent className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-purple-800">{t("reports:commissionsPaid")}</p>
                      <p className="text-xs text-purple-600">{t("employees:toTrainersAndEmployees")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-purple-700">-{formatCurrency(commissionsReport?.summary?.total_paid_commission || 0)}</p>
                </div>
                
                {/* Refunds */}
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <RefreshCcw className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="font-medium text-red-800">{t("common:i̇adeler")}</p>
                      <p className="text-xs text-red-600">{refundsReport?.summary?.refund_count || 0} {t("common:refundsCount")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-red-700">-{formatCurrency(refundsReport?.summary?.total_refunds || 0)}</p>
                </div>
                
                {/* Total Expenses */}
                <div className="flex items-center justify-between p-3 bg-red-100 rounded-lg border border-red-200">
                  <p className="font-bold text-red-800">{t("common:toplamGi̇der")}</p>
                  <p className="font-bold text-xl text-red-700">
                    -{formatCurrency(
                      (expensesReport?.summary?.total_expenses || 0) + 
                      (salariesReport?.summary?.total_paid_salary || 0) + 
                      (commissionsReport?.summary?.total_paid_commission || 0) +
                      (refundsReport?.summary?.total_refunds || 0)
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* NET PROFIT/LOSS */}
          <Card className="border-0 shadow-card mt-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${
                    ((salesReport?.summary?.net_received || 0) + 
                     (trainerIncomeReport?.rent_income || 0) + 
                     (trainerIncomeReport?.commission_income || 0) -
                     (expensesReport?.summary?.total_expenses || 0) - 
                     (salariesReport?.summary?.total_paid_salary || 0) - 
                     (commissionsReport?.summary?.total_paid_commission || 0) -
                     (refundsReport?.summary?.total_refunds || 0)) >= 0 
                      ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {((salesReport?.summary?.net_received || 0) + 
                      (trainerIncomeReport?.rent_income || 0) + 
                      (trainerIncomeReport?.commission_income || 0) -
                      (expensesReport?.summary?.total_expenses || 0) - 
                      (salariesReport?.summary?.total_paid_salary || 0) - 
                      (commissionsReport?.summary?.total_paid_commission || 0) -
                      (refundsReport?.summary?.total_refunds || 0)) >= 0 
                        ? <TrendingUp className="w-6 h-6 text-green-600" />
                        : <TrendingDown className="w-6 h-6 text-red-600" />
                    }
                  </div>
                  <div>
                    <p className="text-grey text-sm">{t("common:netProfitLoss")}</p>
                    <p className="text-xs text-grey">{t("common:selectedPeriod")} {formatDateForAPI(startDate)} - {formatDateForAPI(endDate)}</p>
                  </div>
                </div>
                <p className={`font-bold text-3xl ${
                  ((salesReport?.summary?.net_received || 0) + 
                   (trainerIncomeReport?.rent_income || 0) + 
                   (trainerIncomeReport?.commission_income || 0) -
                   (expensesReport?.summary?.total_expenses || 0) - 
                   (salariesReport?.summary?.total_paid_salary || 0) - 
                   (commissionsReport?.summary?.total_paid_commission || 0) -
                   (refundsReport?.summary?.total_refunds || 0)) >= 0 
                    ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(
                    (salesReport?.summary?.net_received || 0) + 
                    (trainerIncomeReport?.rent_income || 0) + 
                    (trainerIncomeReport?.commission_income || 0) -
                    (expensesReport?.summary?.total_expenses || 0) - 
                    (salariesReport?.summary?.total_paid_salary || 0) - 
                    (commissionsReport?.summary?.total_paid_commission || 0) -
                    (refundsReport?.summary?.total_refunds || 0)
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Report - Paket Satışı */}
        <TabsContent value="sales" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                {t("packages:packageSalesReport")}
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                <ExportButton reportType="sales" onExport={handleExport} />
                <Badge variant="outline" className="text-blue-600">
                  {salesReport?.summary?.total_sales_count || 0} {t("packages:packagesCount")}
                </Badge>
                <Badge className="bg-green-100 text-green-700">
                  {t("common:total")}: {formatCurrency(salesReport?.summary?.total_sales_amount)} / {t("common:paidColon")}: {formatCurrency(salesReport?.summary?.total_paid_amount)}
                </Badge>
                {salesReport?.summary?.total_pending_amount > 0 && (
                  <Badge className="bg-orange-100 text-orange-700">
                    {t("common:pendingLabel")}: {formatCurrency(salesReport?.summary?.total_pending_amount)}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Sales Summary */}
              <div className="mb-4 p-3 bg-slate-50 rounded-xl">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                  <div>
                    <p className="text-grey text-xs">{t("common:brutSatis")}</p>
                    <p className="font-semibold text-navy">{formatCurrency(salesReport?.summary?.total_sales_amount)}</p>
                  </div>
                  <div>
                    <p className="text-grey text-xs">{t("payments:collection")}</p>
                    <p className="font-semibold text-green-600">{formatCurrency(salesReport?.summary?.total_paid_amount)}</p>
                  </div>
                  <div>
                    <p className="text-grey text-xs">{t("common:pending")}</p>
                    <p className="font-semibold text-amber-600">{formatCurrency(salesReport?.summary?.total_pending_amount)}</p>
                  </div>
                  <div>
                    <p className="text-grey text-xs">{t("common:refund")}</p>
                    <p className="font-semibold text-red-600">-{formatCurrency(salesReport?.summary?.total_refunded_amount)}</p>
                  </div>
                  <div>
                    <p className="text-grey text-xs">{t('common:netRevenue')}</p>
                    <p className="font-bold text-navy">{formatCurrency(salesReport?.summary?.net_received)}</p>
                  </div>
                </div>
              </div>
              
              {/* Sales by Branch */}
              {salesReport?.by_branch && Object.keys(salesReport.by_branch).length > 0 && (
                <div className="mb-4 p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey mb-2">{t("common:branslaraGore")}</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(salesReport.by_branch).map(([branch, data]) => (
                      <Badge key={branch} variant="secondary" className="text-sm">
                        {branch}: {data.count} paket ({formatCurrency(data.amount)})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common:date')}</TableHead>
                      <TableHead>Paket No</TableHead>
                      <TableHead>{t("common:member")}</TableHead>
                      <TableHead>{t("packages:package")}</TableHead>
                      <TableHead>{t("common:branch")}</TableHead>
                      <TableHead>{t("common:method")}</TableHead>
                      <TableHead>{t('common:status')}</TableHead>
                      <TableHead className="text-right">{t('common:price')}</TableHead>
                      <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                      <TableHead className="text-right">{t("common:refund")}</TableHead>
                      <TableHead className="text-right">{t('packages:remaining')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesReport?.sales?.map((sale, idx) => {
                      const refundedAmount = sale.refunded_amount || 0;
                      const isCancelled = sale.status === 'cancelled';
                      const isRefundCancel = isCancelled && sale.cancel_type === 'refund';
                      const isPaidCancel = isCancelled && (sale.cancel_type === 'paid_cancel' || sale.keep_debt === true);
                      const isFreeCancel = isCancelled && !isRefundCancel && !isPaidCancel;
                      const remainingAmount = sale.remaining_amount || 0;
                      
                      return (
                        <TableRow key={idx} className={isCancelled ? 'bg-red-50' : ''}>
                          <TableCell className="text-sm">{formatDate(sale.sold_at)}</TableCell>
                          <TableCell>
                            <span className="font-mono text-xs text-navy">{sale.package_no || "-"}</span>
                          </TableCell>
                          <TableCell className="font-medium">{sale.member_name}</TableCell>
                          <TableCell className={isCancelled ? 'text-grey' : ''}>
                            {sale.package_name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs">{getLocalizedBranch(sale.branch)}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {sale.payment_method === 'cash' ? t('payments:cash') :
                               sale.payment_method === 'credit_card' ? t('payments:creditCard') :
                               sale.payment_method === 'bank_transfer' ? t('payments:bankTransfer') :
                               sale.payment_method === 'eft' ? t('payments:eft') :
                               sale.payment_method ? sale.payment_method : t('payments:cash')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {isRefundCancel ? (
                              <Badge className="bg-red-100 text-red-700 text-xs">{t('members:refundCancelled')}</Badge>
                            ) : isPaidCancel ? (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">Ücretli İptal</Badge>
                            ) : isFreeCancel ? (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">{t('members:freeCancellation')}</Badge>
                            ) : sale.status === 'active' ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">{t('admin:active')}</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">{sale.status}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {isRefundCancel ? (
                              <div>
                                <span className="line-through text-grey text-sm">{formatCurrency(sale.original_price || sale.price)}</span>
                                <br/>
                                <span className="font-medium text-navy">{formatCurrency(sale.effective_price || (sale.paid_amount - refundedAmount))}</span>
                              </div>
                            ) : (
                              <span className="font-medium">{formatCurrency(sale.price)}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(sale.paid_amount)}</TableCell>
                          <TableCell className="text-right">
                            {refundedAmount > 0 ? (
                              <span className="text-red-600">-{formatCurrency(refundedAmount)}</span>
                            ) : (
                              <span className="text-grey">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {remainingAmount > 0 ? (
                              <span className="text-orange-600 font-medium">{formatCurrency(remainingAmount)}</span>
                            ) : (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                {isRefundCancel ? t("common:closed") : t("common:paidStatus")}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(!salesReport?.sales || salesReport.sales.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-grey">
                          {t("common:noSalesRecordThisPeriod")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trainer Income - Gelen Kira Report */}
        <TabsContent value="trainer-income" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-amber-700 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {t("reports:incomingRentReport")}
              </CardTitle>
              <div className="flex gap-2">
                <ExportButton reportType="trainer-income" onExport={handleExport} />
                <Badge className="bg-amber-100 text-amber-700">
                  {t("common:pendingLabel")}: {formatCurrency(trainerIncomeReport?.rent_pending || 0)}
                </Badge>
                <Badge className="bg-green-100 text-green-700">
                  {t("common:collectedRent")}: {formatCurrency(trainerIncomeReport?.rent_income || 0)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary Cards - Sadece Kira */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Card className="bg-amber-50 border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Clock className="w-8 h-8 text-amber-600" />
                      <div>
                        <p className="text-sm text-amber-600">{t("common:pendingRent")}</p>
                        <p className="text-xl font-bold text-amber-700">{formatCurrency(pendingRents?.total_rent || 0)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                      <div>
                        <p className="text-sm text-green-600">{t("common:collectedRent")}</p>
                        <p className="text-xl font-bold text-green-700">{formatCurrency(trainerIncomeReport?.rent_income || 0)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Rent Payments Table */}
              {trainerIncomeReport?.rent_payments?.length > 0 ? (
                <div className="mb-6">
                  <h4 className="font-medium text-amber-700 mb-3">{t('payments:rentCollections')}</h4>
                  <div className="rounded-xl border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-50">
                          <TableHead>{t('common:date')}</TableHead>
                          <TableHead>{t('common:trainer')}</TableHead>
                          <TableHead>{t("common:period")}</TableHead>
                          <TableHead>{t("common:method")}</TableHead>
                          <TableHead className="text-right">{t('common:amount')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {trainerIncomeReport.rent_payments.map((payment, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{payment.payment_date}</TableCell>
                            <TableCell>{payment.trainer_name || '-'}</TableCell>
                            <TableCell>{payment.period || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {payment.payment_method === 'cash' ? t('payments:cash') :
                                 payment.payment_method === 'credit_card' ? t('payments:creditCard') :
                                 payment.payment_method === 'bank_transfer' ? t('payments:bankTransfer') :
                                 payment.payment_method === 'eft' ? t('payments:eft') :
                                 payment.payment_method ? payment.payment_method : t('payments:cash')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-medium text-green-700">{formatCurrency(payment.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-grey">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-grey/50" />
                  <p>{t("common:buDonemdeKiraTahsilatiBulunmuyor")}</p>
                </div>
              )}

              {/* Pending Rents */}
              {pendingRents?.rents && pendingRents.rents.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {t("reports:pendingRents", "Bekleyen Kira Tahsilatları")}
                    </h4>
                    <Badge className="bg-amber-100 text-amber-700">
                      {formatCurrency(pendingRents.total_rent || 0)}
                    </Badge>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-amber-100">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-50">
                          <TableHead>{t('common:trainer')}</TableHead>
                          <TableHead>{t("common:period")}</TableHead>
                          <TableHead>{t('common:dueDate')}</TableHead>
                          <TableHead className="text-right">{t('common:amount')}</TableHead>
                          <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                          <TableHead className="text-right">{t("common:remaining")}</TableHead>
                          <TableHead className="text-right">İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingRents.rents.map((item, idx) => (
                          <TableRow key={`pending-rent-${idx}`}>
                            <TableCell className="font-medium">{item.trainer_name}</TableCell>
                            <TableCell>{item.month && item.year ? `${item.month}/${item.year}` : "-"}</TableCell>
                            <TableCell>{item.due_date ? formatDate(item.due_date) : "-"}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                            <TableCell className="text-right text-green-600">{formatCurrency(item.paid_amount)}</TableCell>
                            <TableCell className="text-right text-amber-700 font-semibold">{formatCurrency(item.remaining_amount)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                className="h-7 px-2 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => openQuickPay("rent", item)}
                                data-testid={`quickpay-rent-${idx}`}
                              >
                                <Zap className="w-3 h-3 mr-1" />
                                Tahsil Et
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commissions Report - Hem Ödenen hem Verilen (Alınan) Primler */}
        <TabsContent value="commission-income" className="mt-4 space-y-6">
          {/* GELEN PRİMLER - Prim veren antrenörlerden işletmeye gelen */}
          <Card className="border-0 shadow-sm border-l-4 border-l-green-500">
            <CardHeader className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base sm:text-lg font-bold text-green-700 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="break-words">{t("reports:incomingCommissions")}</span>
                </CardTitle>
                <div className="flex flex-wrap gap-1 sm:gap-2">
                  <ExportButton reportType="commissions-received" onExport={handleExport} />
                  <Badge variant="outline" className="text-green-600 text-xs">
                    {commissionsReport?.received_summary?.total_sessions || 0} {t("common:lessonLabel")}
                  </Badge>
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    {formatCurrency(commissionsReport?.received_summary?.total_calculated_commission)}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
                <div className="p-2 sm:p-3 bg-green-50 rounded-xl text-center">
                  <p className="text-[10px] sm:text-xs text-green-700 mb-1">{t("admin:calculated")}</p>
                  <p className="font-bold text-green-700 text-sm sm:text-base">{formatCurrency(commissionsReport?.received_summary?.total_calculated_commission)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-emerald-50 rounded-xl text-center">
                  <p className="text-[10px] sm:text-xs text-emerald-700 mb-1">{t("common:collected")}</p>
                  <p className="font-bold text-emerald-700 text-sm sm:text-base">{formatCurrency(commissionsReport?.received_summary?.total_paid_commission)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-amber-50 rounded-xl text-center">
                  <p className="text-[10px] sm:text-xs text-amber-700 mb-1">{t("common:pending")}</p>
                  <p className="font-bold text-amber-700 text-sm sm:text-base">{formatCurrency(commissionsReport?.received_summary?.total_pending_commission)}</p>
                </div>
              </div>

              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <Table className="min-w-[500px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">{t('common:trainer')}</TableHead>
                      <TableHead className="text-right text-xs">{t("common:lessonsSingular")}</TableHead>
                      <TableHead className="text-right text-xs">{t("admin:calculated")}</TableHead>
                      <TableHead className="text-right text-xs">{t("common:collected")}</TableHead>
                      <TableHead className="text-right text-xs">{t("common:pending")}</TableHead>
                      <TableHead className="text-right text-xs">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissionsReport?.received_by_trainer?.map((trainer, idx) => {
                      // Fallback: backend may not include pending_commission on items.
                      // Compute it as calculated - paid to avoid showing "Collected" incorrectly.
                      const pending = (typeof trainer.pending_commission === "number")
                        ? trainer.pending_commission
                        : ((trainer.calculated_commission || 0) - (trainer.paid_commission || 0));
                      return (
                      <TableRow key={idx}>
                        <TableCell className="font-medium text-xs sm:text-sm">{trainer.trainer_name}</TableCell>
                        <TableCell className="text-right text-xs sm:text-sm">{trainer.sessions}</TableCell>
                        <TableCell className="text-right text-green-600 text-xs sm:text-sm">{formatCurrency(trainer.calculated_commission)}</TableCell>
                        <TableCell className="text-right text-emerald-600 text-xs sm:text-sm">{formatCurrency(trainer.paid_commission)}</TableCell>
                        <TableCell className="text-right text-xs sm:text-sm">
                          {pending > 0 ? (
                            <span className="text-amber-600">{formatCurrency(pending)}</span>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 text-[10px] sm:text-xs">{t("common:collectedStatus")}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {pending > 0 && (
                            <Button
                              size="sm"
                              className="h-7 px-2 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => openQuickPay("rent", { ...trainer, remaining_amount: pending, debt_id: trainer.debt_id })}
                              data-testid={`quickpay-incoming-comm-${idx}`}
                              title="Antrenörden prim tahsil et"
                            >
                              <Zap className="w-3 h-3 mr-1" />
                              Tahsil Et
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {(!commissionsReport?.received_by_trainer || commissionsReport.received_by_trainer.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-grey">
                          {t("reports:noIncomingCommRecordThisPeriod")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PRIMLER (VERİLEN) - Trainers who receive commissions FROM the business */}
        <TabsContent value="commissions" className="mt-4 space-y-6">
          {/* ÖDENEN PRİMLER - Prim alan antrenörlere işletmeden ödenen */}
          <Card className="border-0 shadow-sm border-l-4 border-l-purple-500">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-purple-700 flex items-center gap-2">
                <TrendingDown className="w-5 h-5" />
                {t("reports:paidCommissions")}
              </CardTitle>
              <div className="flex gap-2">
                <ExportButton reportType="commissions" onExport={handleExport} />
                <Badge variant="outline" className="text-purple-600">
                  {commissionsReport?.summary?.total_sessions || 0} {t("common:lessonLabel")}
                </Badge>
                <Badge className="bg-purple-100 text-purple-700">
                  {formatCurrency(commissionsReport?.summary?.total_calculated_commission)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-purple-50 rounded-xl text-center">
                  <p className="text-xs text-purple-700 mb-1">{t("admin:calculated")}</p>
                  <p className="font-bold text-purple-700">{formatCurrency(commissionsReport?.summary?.total_calculated_commission)}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl text-center">
                  <p className="text-xs text-green-700 mb-1">{t("common:paidAmount")}</p>
                  <p className="font-bold text-green-700">{formatCurrency(commissionsReport?.summary?.total_paid_commission)}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-xl text-center">
                  <p className="text-xs text-orange-700 mb-1">{t("common:pending")}</p>
                  <p className="font-bold text-orange-700">{formatCurrency(commissionsReport?.summary?.total_pending_commission)}</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">{t('common:trainer')}</TableHead>
                      <TableHead className="text-right w-[100px]">{t("common:lessonsSingular")}</TableHead>
                      <TableHead className="text-right w-[120px]">{t("admin:calculated")}</TableHead>
                      <TableHead className="text-right w-[120px]">{t("common:paidAmount")}</TableHead>
                      <TableHead className="text-right w-[120px]">{t("common:pending")}</TableHead>
                      <TableHead className="text-right w-[110px]">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissionsReport?.by_trainer?.map((trainer, idx) => {
                      const pendingComm = (trainer.calculated_commission || 0) - (trainer.paid_commission || 0);
                      return (
                      <TableRow key={`trainer-${idx}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {trainer.trainer_name}
                            <Badge variant="outline" className="text-xs">{t('common:trainer')}</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{trainer.sessions}</TableCell>
                        <TableCell className="text-right text-purple-600">{formatCurrency(trainer.calculated_commission)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(trainer.paid_commission)}</TableCell>
                        <TableCell className="text-right">
                          {pendingComm > 0.01 ? (
                            <span className="text-orange-600">
                              {formatCurrency(pendingComm)}
                            </span>
                          ) : pendingComm < -0.01 ? (
                            <Badge className="bg-blue-100 text-blue-700 text-xs" data-testid={`trainer-overpaid-${idx}`}>Fazla ödeme {formatCurrency(-pendingComm)}</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 text-xs">{t('payments:paid')}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {pendingComm > 0.01 && (
                            <Button
                              size="sm"
                              className="h-7 px-2 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => openQuickPay("commission", { ...trainer, pending_commission: pendingComm })}
                              data-testid={`quickpay-commission-${idx}`}
                            >
                              <Zap className="w-3 h-3 mr-1" />
                              Hızlı Öde
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {commissionsReport?.by_employee?.map((employee, idx) => {
                      const pendingEmp = (employee.calculated_commission || 0) - (employee.paid_commission || 0);
                      return (
                      <TableRow key={`employee-${idx}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {employee.employee_name}
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">Çalışan</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{employee.sessions}</TableCell>
                        <TableCell className="text-right text-purple-600">{formatCurrency(employee.calculated_commission)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(employee.paid_commission)}</TableCell>
                        <TableCell className="text-right">
                          {pendingEmp > 0 ? (
                            <span className="text-orange-600">
                              {formatCurrency(pendingEmp)}
                            </span>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 text-xs">{t('payments:paid')}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {pendingEmp > 0 && (
                            <Button
                              size="sm"
                              className="h-7 px-2 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`quickpay-employee-comm-${idx}`}
                              title="Hızlı ödeme için Çalışan modülünden yapın"
                              disabled
                            >
                              <Zap className="w-3 h-3 mr-1" />
                              Hızlı Öde
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {(!commissionsReport?.by_trainer || commissionsReport.by_trainer.length === 0) && 
                     (!commissionsReport?.by_employee || commissionsReport.by_employee.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-grey">
                          {t("reports:noCommissionRecordThisPeriod")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Salaries Report */}
        <TabsContent value="salaries" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
                <Banknote className="w-5 h-5" />
                {t("reports:salaryReport")}
              </CardTitle>
              <div className="flex gap-2">
                <ExportButton reportType="salaries" onExport={handleExport} />
                <Badge variant="outline" className="text-orange-600">
                  {salariesReport?.by_trainer?.length || 0} {t("common:trainersCount")}
                </Badge>
                <Badge className="bg-orange-100 text-orange-700">
                  {formatCurrency(salariesReport?.summary?.total_paid_salary)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-orange-50 rounded-xl text-center">
                  <p className="text-xs text-orange-700 mb-1">{t("common:total")}</p>
                  <p className="font-bold text-orange-700">{formatCurrency((salariesReport?.summary?.total_paid_salary || 0) + (salariesReport?.summary?.total_pending_salary || 0))}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl text-center">
                  <p className="text-xs text-green-700 mb-1">{t("employees:odenenMaas")}</p>
                  <p className="font-bold text-green-700">{formatCurrency(salariesReport?.summary?.total_paid_salary)}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-xl text-center">
                  <p className="text-xs text-red-700 mb-1">{t("employees:pendingSalary")}</p>
                  <p className="font-bold text-red-700">{formatCurrency(salariesReport?.summary?.total_pending_salary)}</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common:trainer')}</TableHead>
                      <TableHead>{t('trainers:workType')}</TableHead>
                      <TableHead className="text-right">{t('trainers:salary')}</TableHead>
                      <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                      <TableHead className="text-right">{t("common:pending")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salariesReport?.by_trainer?.map((trainer, idx) => {
                      const typeLabels = {
                        salary_only: t("employees:salaryOnly"),
                        salary_plus_commission: t("reports:salaryPlusCommission")
                      };
                      return (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{trainer.trainer_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs">
                              {typeLabels[trainer.salary_type] || trainer.salary_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(trainer.current_salary)}</TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(trainer.paid_amount)}</TableCell>
                          <TableCell className="text-right">
                            {trainer.pending_amount > 0 ? (
                              <span className="text-orange-600">{formatCurrency(trainer.pending_amount)}</span>
                            ) : (
                              <Badge className="bg-green-100 text-green-700 text-xs">{t('payments:paid')}</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(!salariesReport?.by_trainer || salariesReport.by_trainer.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-grey">
                          {t("common:noSalariedTrainer")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pending Salary Debts */}
              {salariesReport?.pending && salariesReport.pending.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {t("reports:pendingSalaries", "Bekleyen Maaş Ödemeleri")}
                    </h4>
                    <Badge className="bg-amber-100 text-amber-700">
                      {formatCurrency(salariesReport.summary.total_pending_salary)}
                    </Badge>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-amber-100">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-50">
                          <TableHead>{t('common:name')}</TableHead>
                          <TableHead>{t('common:period')}</TableHead>
                          <TableHead>{t('common:dueDate')}</TableHead>
                          <TableHead className="text-right">{t('common:amount')}</TableHead>
                          <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                          <TableHead className="text-right">{t("common:remaining")}</TableHead>
                          <TableHead className="text-right">İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {salariesReport.pending.map((item, idx) => (
                          <TableRow key={`pending-salary-${idx}`}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell>{item.month && item.year ? `${item.month}/${item.year}` : "-"}</TableCell>
                            <TableCell>{item.due_date ? formatDate(item.due_date) : "-"}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                            <TableCell className="text-right text-green-600">{formatCurrency(item.paid_amount)}</TableCell>
                            <TableCell className="text-right text-amber-700 font-semibold">{formatCurrency(item.remaining_amount)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                className="h-7 px-2 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => openQuickPay("salary", item)}
                                data-testid={`quickpay-salary-${idx}`}
                              >
                                <Zap className="w-3 h-3 mr-1" />
                                Hızlı Öde
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bonus / Extra Payments */}
        <TabsContent value="bonus" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-purple-700 flex items-center gap-2">
                <Gift className="w-5 h-5" />
                {t('employees:bonusPayments')}
              </CardTitle>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-purple-600">
                  {bonusReport?.summary?.count || 0}
                </Badge>
                <Badge className="bg-purple-100 text-purple-700">
                  {formatCurrency(bonusReport?.summary?.total_paid || 0)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {bonusReport?.items && bonusReport.items.length > 0 ? (
                <div className="rounded-xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-purple-50">
                        <TableHead>{t('common:date')}</TableHead>
                        <TableHead>{t('common:name')}</TableHead>
                        <TableHead>{t('common:typeLabel')}</TableHead>
                        <TableHead>{t('payments:paymentMethod')}</TableHead>
                        <TableHead>{t('common:reason')}</TableHead>
                        <TableHead className="text-right">{t('common:amount')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bonusReport.items.map((item, idx) => (
                        <TableRow key={`bonus-${idx}`}>
                          <TableCell>{item.payment_date ? formatDate(item.payment_date) : "-"}</TableCell>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {item.entity_type === 'employee' ? t('common:employee') : t('common:trainer')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {item.payment_method === 'cash' ? t('payments:cash') :
                               item.payment_method === 'credit_card' ? t('payments:creditCard') :
                               item.payment_method === 'bank_transfer' ? t('payments:bankTransfer') :
                               item.payment_method === 'eft' ? t('payments:eft') :
                               item.payment_method ? item.payment_method : t('payments:cash')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-grey">{item.reason || "-"}</TableCell>
                          <TableCell className="text-right font-semibold text-purple-700">{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-grey">
                  <Gift className="w-12 h-12 mx-auto mb-3 text-grey/50" />
                  <p>{t("reports:noBonusThisPeriod", "Bu dönemde ek ödeme bulunmuyor")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="refunds" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
                <RefreshCcw className="w-5 h-5" />
                {t("reports:refundReport")}
              </CardTitle>
              <div className="flex gap-2">
                <ExportButton reportType="refunds" onExport={handleExport} />
                <Badge variant="outline" className="text-red-600">
                  {refundsReport?.summary?.refund_count || 0} {t("common:refund")}
                </Badge>
                <Badge className="bg-red-100 text-red-700">
                  {formatCurrency(refundsReport?.summary?.total_refunds)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common:date')}</TableHead>
                      <TableHead>{t("common:member")}</TableHead>
                      <TableHead>{t("common:typeLabel")}</TableHead>
                      <TableHead>{t("common:reason")}</TableHead>
                      <TableHead className="text-right">{t('common:amount')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {refundsReport?.refunds?.map((refund, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">{formatDate(refund.created_at)}</TableCell>
                        <TableCell className="font-medium">{refund.member_name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {refund.type === "payment_refund" ? t("common:paymentRefund") : "Paket İptali"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-grey max-w-[200px] truncate">
                          {refund.reason || refund.package_name || "-"}
                        </TableCell>
                        <TableCell className="text-right font-bold text-red-600">
                          -{formatCurrency(refund.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!refundsReport?.refunds || refundsReport.refunds.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-grey">
                          {t("common:noRefundRecordThisPeriod")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Report */}
        <TabsContent value="expenses" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                {t('payments:businessExpenses')}
              </CardTitle>
              <div className="flex gap-2">
                {/* Expense Report Title & Title is set above. Update title to "İşletme Giderleri" */}
                <ExportButton reportType="expenses" onExport={handleExport} />
                <Badge variant="outline" className="text-slate-600">
                  {expensesReport?.summary?.expense_count || 0} {t("reports:expensesCount")}
                </Badge>
                <Badge className="bg-slate-100 text-slate-700">
                  {formatCurrency(expensesReport?.summary?.total_expenses)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <p className="text-xs text-slate-700 mb-1">{t("common:total")}</p>
                  <p className="font-bold text-slate-700">{formatCurrency((expensesReport?.summary?.total_expenses || 0) + (expensesReport?.summary?.total_pending_expenses || 0))}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl text-center">
                  <p className="text-xs text-green-700 mb-1">{t("common:paidAmount")}</p>
                  <p className="font-bold text-green-700">{formatCurrency(expensesReport?.summary?.total_expenses)}</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl text-center">
                  <p className="text-xs text-amber-700 mb-1">{t("common:pending")}</p>
                  <p className="font-bold text-amber-700">{formatCurrency(expensesReport?.summary?.total_pending_expenses)}</p>
                </div>
              </div>
              {/* By Type Summary */}
              {expensesReport?.by_type && Object.keys(expensesReport.by_type).length > 0 && (
                <div className="mb-4 p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey mb-2">{t("reports:giderTuruneGore")}</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(expensesReport.by_type).map(([typeName, data]) => (
                      <Badge key={typeName} variant="secondary" className="text-sm">
                        {typeName}: {data.count}x ({formatCurrency(data.amount)})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common:date')}</TableHead>
                      <TableHead>{t("common:typeLabel")}</TableHead>
                      <TableHead>{t('common:description')}</TableHead>
                      <TableHead>{t("common:payments")}</TableHead>
                      <TableHead className="text-right">{t('common:amount')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expensesReport?.expenses?.map((expense, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">{formatDate(expense.expense_date)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {getLocalizedExpenseType(expense.expense_type)}
                            {expense.is_recurring && <span className="ml-1">🔄</span>}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-grey max-w-[200px] truncate">
                          {expense.description || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {expense.payment_method === "cash" ? t('payments:cash') :
                             expense.payment_method === "credit_card" ? t('payments:creditCard') :
                             expense.payment_method === "bank_transfer" ? t('payments:bankTransfer') :
                             expense.payment_method === "eft" ? t('payments:eft') :
                             expense.payment_method ? expense.payment_method : t('payments:cash')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold text-slate-600">
                          -{formatCurrency(expense.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!expensesReport?.expenses || expensesReport.expenses.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-grey">
                          {t("reports:noExpenseRecordThisPeriod")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pending Expense Debts */}
              {expensesReport?.pending && expensesReport.pending.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {t("reports:pendingExpenses", "Bekleyen Giderler")}
                    </h4>
                    <Badge className="bg-amber-100 text-amber-700">
                      {formatCurrency(expensesReport.summary.total_pending_expenses)}
                    </Badge>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-amber-100">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-50">
                          <TableHead>{t('common:dueDate')}</TableHead>
                          <TableHead>{t("common:typeLabel")}</TableHead>
                          <TableHead>{t('common:period')}</TableHead>
                          <TableHead className="text-right">{t('common:amount')}</TableHead>
                          <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                          <TableHead className="text-right">{t("common:remaining")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expensesReport.pending.map((item, idx) => (
                          <TableRow key={`pending-expense-${idx}`}>
                            <TableCell>{item.due_date ? formatDate(item.due_date) : "-"}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">
                                {getLocalizedExpenseType(item.expense_type)}
                              </Badge>
                            </TableCell>
                            <TableCell>{item.month && item.year ? `${item.month}/${item.year}` : "-"}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                            <TableCell className="text-right text-green-600">{formatCurrency(item.paid_amount)}</TableCell>
                            <TableCell className="text-right text-amber-700 font-semibold">{formatCurrency(item.remaining_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Quick Payment Dialog */}
      <QuickPaymentDialog
        open={!!quickPayState}
        onOpenChange={(open) => !open && setQuickPayState(null)}
        title={quickPayState?.title || "Hızlı Ödeme"}
        subject={quickPayState?.subject}
        defaultAmount={quickPayState?.defaultAmount || 0}
        maxAmount={quickPayState?.maxAmount}
        onSubmit={handleQuickPaySubmit}
        testIdPrefix="quickpay-reports"
        infoNote={
          quickPayState?.type === "rent"
            ? "Bu kira tahsilatı kaydedilirken trainer borç defteri de güncellenir."
            : quickPayState?.type === "commission"
            ? "Bu prim ödemesi antrenörün primler ekranına kaydedilecek."
            : null
        }
      />
    </div>
  );
}
